import { message } from 'antd'
import { useCallback, useState } from 'react'
import {
  addFoodSafetyCorrectiveAction,
  recordFoodSafetyExceptionRecheck,
} from 'src/utils/foodSafetyApi'
import {
  buildFoodSafetyCorrectiveActionBody,
  canRecordFoodSafetyRecheck,
  initialFoodSafetyFollowUpDraft,
  isFoodSafetyExceptionOpen,
  resolveFoodSafetyRecheckValue,
  validateFoodSafetyCorrectiveDraft,
} from 'src/utils/foodSafetyCorrectiveAction'
import { formatFoodSafetySubmitError } from 'src/utils/foodSafetyFeedback'
import {
  FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE,
  refreshAfterFoodSafetySave,
} from 'src/utils/foodSafetySaveGuard'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'

const without = (map, key) => {
  if (!(key in map)) return map
  const next = { ...map }
  delete next[key]
  return next
}

/**
 * Record a corrective action and / or recheck on an already-saved reading.
 * One instance per screen; drafts are keyed by exception id.
 *
 * @param {{ venueId: string, onSaved?: Function }} options
 */
export default function useFoodSafetyExceptionFollowUps({ venueId, onSaved }) {
  const [drafts, setDrafts] = useState({})
  const [errors, setErrors] = useState({})
  const [savingId, setSavingId] = useState(null)
  const saveLock = useFoodSafetySaveLock()

  const open = useCallback((exception, patch) => {
    if (!exception?._id) return
    setDrafts(prev => ({
      ...prev,
      [exception._id]: initialFoodSafetyFollowUpDraft(exception, patch),
    }))
  }, [])

  const close = useCallback(exceptionId => {
    setDrafts(prev => without(prev, exceptionId))
    setErrors(prev => without(prev, exceptionId))
  }, [])

  const reset = useCallback(() => {
    setDrafts({})
    setErrors({})
  }, [])

  const update = useCallback((exceptionId, patch) => {
    setErrors(prev => without(prev, exceptionId))
    setDrafts(prev => ({
      ...prev,
      [exceptionId]: { ...(prev[exceptionId] || {}), ...patch },
    }))
  }, [])

  const save = async (exception, { viewingToday = false } = {}) => {
    const exceptionId = exception._id
    if (saveLock.isHeld(exceptionId)) return false
    const draft = drafts[exceptionId] || {}
    const actionable = isFoodSafetyExceptionOpen(exception)
    const correctiveAction = actionable
      ? buildFoodSafetyCorrectiveActionBody(draft)
      : null
    const recheckValue = canRecordFoodSafetyRecheck(exception, { viewingToday })
      ? resolveFoodSafetyRecheckValue(draft)
      : null
    const draftError =
      validateFoodSafetyCorrectiveDraft(draft) ||
      (!correctiveAction && recheckValue == null
        ? actionable
          ? 'Choose what was done.'
          : 'Enter the recheck temperature.'
        : null)
    if (draftError) {
      setErrors(prev => ({ ...prev, [exceptionId]: draftError }))
      return false
    }

    if (!saveLock.tryAcquire(exceptionId)) return false
    setSavingId(exceptionId)
    try {
      let recheckFailed = null
      try {
        if (correctiveAction) {
          await addFoodSafetyCorrectiveAction(
            venueId,
            exceptionId,
            correctiveAction,
          )
        }
        if (recheckValue != null) {
          try {
            await recordFoodSafetyExceptionRecheck(venueId, exceptionId, {
              value: recheckValue,
            })
          } catch (recheckError) {
            if (!correctiveAction) throw recheckError
            recheckFailed = recheckError
          }
        }
      } catch (error) {
        setErrors(prev => ({
          ...prev,
          [exceptionId]: formatFoodSafetySubmitError(error),
        }))
        return false
      }
      if (recheckFailed) {
        message.warning(
          `Action recorded, but the recheck didn’t save — ${formatFoodSafetySubmitError(
            recheckFailed,
          )}`,
        )
      } else {
        message.success(
          correctiveAction ? 'Corrective action recorded' : 'Recheck recorded',
        )
      }
      close(exceptionId)
      if (!(await refreshAfterFoodSafetySave(onSaved))) {
        message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
      }
      return true
    } finally {
      saveLock.release(exceptionId)
      setSavingId(null)
    }
  }

  return { drafts, errors, savingId, open, close, reset, update, save }
}
